import { cn } from "@lib/cn"
import { ChevronRight } from "lucide-react"
import type { ReactNode } from "react"

export type StatTone = "neutral" | "emerald" | "amber" | "indigo" | "red"

const VALUE_TONE: Record<StatTone, string> = {
  neutral: "text-ink",
  emerald: "text-success-ink",
  amber: "text-attention-ink",
  indigo: "text-accent-ink",
  red: "text-danger-ink",
}

interface Props {
  label: string
  value: ReactNode
  /** One line under the value: a comparison, a deadline, a count. */
  hint?: ReactNode
  tone?: StatTone
  /** Renders the tile as a button. Omit for a read-only readout. */
  onClick?: () => void
  className?: string
  /** Escape hatch for data-driven value colours (grade tiers). Wins over `tone`. */
  valueClassName?: string
  /** `inset` when the tile sits inside a white card: a tinted ground instead of a second card border. */
  variant?: "card" | "inset"
}

/**
 * One number with its label (PRD-58). The single KPI tile for every
 * "four figures in a row" strip — material results, review-queue
 * summary, student KPIs — so they read as one shape.
 *
 * A `<div>` unless `onClick` is given, in which case it is a real
 * `<button>` (accessibility.md §1): never a div with a click handler.
 */
export function StatTile({
  label,
  value,
  hint,
  tone = "neutral",
  onClick,
  className,
  valueClassName,
  variant = "card",
}: Props) {
  const box = cn(
    "rounded-card flex min-h-11 flex-col items-start px-3 py-2 text-left",
    variant === "card" ? "border border-border bg-surface" : "bg-inset",
    onClick && "transition-colors duration-150 hover:bg-accent-soft",
    className,
  )
  const content = (
    <>
      <span className="text-label inline-flex items-center gap-1">
        {label}
        {onClick ? <ChevronRight className="h-3 w-3" aria-hidden /> : null}
      </span>
      <span className={cn("text-figure", VALUE_TONE[tone], valueClassName)}>{value}</span>
      {hint ? <span className="text-meta inline-flex items-center gap-1">{hint}</span> : null}
    </>
  )
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cn(box, "w-full")}>
        {content}
      </button>
    )
  }
  return <div className={box}>{content}</div>
}
